/*
 * Field Mode's tasks (docs/UI_HUBS_AND_GESTURES_PLAN.md section 5; plan
 * phase 6): what the Tasks sheet lists and the camera's Tasks (n) chip counts.
 *
 *   assigned   the person's open assignments (GET /missions/mine `assigned`,
 *              migration 0100), each with its mission's card
 *   missions   their own open cards (accepted, in progress, submitted) that
 *              no assignment already lists
 *   events     the live field events they take part in or run, live first
 *
 * Missions are asked for only with mission:play, events only with an event
 * scope; a person with neither has no tasks, and no chip.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import api from './api';
import { can } from './permissions';

/** A card the player still holds. */
export const OPEN_PROGRESS = Object.freeze(['accepted', 'in_progress', 'submitted']);
/** An event that is still a task: live first, then finishing, then the lobby. */
const PHASE_ORDER = Object.freeze({ live: 0, finishing: 1, lobby: 2 });
/** Where a desk mission is done: the lab's bench tools (the former mini-games) and the microscope game, by mission kind. */
export const GAME_ROUTES = Object.freeze({
  identify_photos: '/tools/verification', tag_photos: '/tools/annotation', annotate_slides: '/game/microscope',
  review_items: '/game/review',
});

export const playsMissions = (user) => can(user, 'mission:play');
export const seesEvents = (user) => ['event:play', 'event:organise', 'event:read'].some((scope) => can(user, scope));
export const mayHaveTasks = (user) => playsMissions(user) || seesEvents(user);

/** The events that are this person's to go to: taken part in (approved or
 *  waiting) or run by them, not over, live first. An event admin's view of
 *  every event is the Admin hub's, not a task. */
export function yourEvents(items) {
  return (items || [])
    .filter((e) => e && e.phase in PHASE_ORDER)
    .filter((e) => ['approved', 'pending'].includes(e.me?.status) || ['organiser', 'staff'].includes(e.manage))
    .map((e, i) => [e, i])
    .sort(([a, i], [b, j]) => PHASE_ORDER[a.phase] - PHASE_ORDER[b.phase] || i - j)
    .map(([e]) => e);
}

/** Their own open cards that no assignment already lists. */
export function yourMissions(mine, assigned) {
  const listed = new Set((assigned || []).map((a) => a.mission_id));
  return (mine || []).filter((m) => OPEN_PROGRESS.includes(m.progress?.status) && !listed.has(m.mission_id));
}

export function taskCount({ assigned = [], missions = [], events = [] } = {}) {
  return assigned.length + missions.length + events.length;
}

const EMPTY = Object.freeze({ state: 'idle', failed: false, assigned: [], missions: [], events: [], rank: null });

/**
 * The person's tasks, asked for while `enabled` (the camera does not ask
 * with an expired session: a 401 would sign the person out). `reload()`
 * asks again and keeps what is shown meanwhile.
 */
export function useFieldTasks(user, { enabled = true } = {}) {
  const missionsToo = playsMissions(user);
  const eventsToo = seesEvents(user);
  const available = missionsToo || eventsToo;
  const [data, setData] = useState(EMPTY);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const reload = useCallback(() => {
    if (!available) return Promise.resolve();
    setData((d) => (d.state === 'ready' ? d : { ...d, state: 'loading' }));
    return Promise.allSettled([
      missionsToo ? api.get('/missions/mine') : Promise.resolve({ data: {} }),
      eventsToo ? api.get('/events') : Promise.resolve({ data: {} }),
    ]).then(([mine, events]) => {
      if (!alive.current) return;
      const m = mine.status === 'fulfilled' ? mine.value?.data || {} : {};
      const e = events.status === 'fulfilled' ? events.value?.data || {} : {};
      const assigned = (Array.isArray(m.assigned) ? m.assigned : []).filter((a) => a?.mission);
      setData({
        state: 'ready',
        failed: mine.status === 'rejected' || events.status === 'rejected',
        assigned,
        missions: yourMissions(Array.isArray(m.items) ? m.items : [], assigned),
        events: yourEvents(Array.isArray(e.items) ? e.items : []),
        rank: m.rank || null,
      });
    });
  }, [available, missionsToo, eventsToo]);

  useEffect(() => { if (enabled && available) reload(); }, [enabled, available, reload]);

  return { ...data, available, count: taskCount(data), reload };
}
